import React, { SyntheticEvent, useContext, useState } from "react";

import {
  Alert,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { syncWithZuora } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ZuoraLogoSvg from "assets/zuora-logo";
import ConfirmDialog from "components/shared/confirm-dialog";
import { DEFAULT_SNACKBAR_DURATION, EventTypeReason } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { UrlParamTypes } from "types/org-display";

/**
 * The Zuora mark, which re-syncs all of the org's billing data when clicked.
 *
 * A sync can overwrite the subscription's Zuora identifiers, so it always goes
 * through the confirmation - including the four-field override that support uses
 * to point a subscription at the right Zuora account. The subscription detail
 * pane and the organisation summary strip both offer it, so the flow lives here
 * rather than being reimplemented behind the second entry point.
 */
export interface SyncZuoraButtonProps {
  subscriptionId: string;
  /** Reloads the org once the sync returns, so the page shows fresh data. */
  refreshOrgData: () => void;
  iconSize?: number;
  testId?: string;
}

const SyncZuoraButton = ({
  subscriptionId,
  refreshOrgData,
  iconSize = 30,
  testId = "sync-zuora-btn",
}: SyncZuoraButtonProps) => {
  const { roleId, regionCode } = useParams<UrlParamTypes>();
  const { showError } = useContext(SnackbarContext);
  const [showSyncLoading, setShowSyncLoading] = useState(false);
  const [openSyncConfirmDialog, setOpenSyncConfirmDialog] = useState(false);
  const [openSyncSuccessAlert, setOpenSyncSuccessAlert] = useState(false);
  const [showZuoraOverride, setShowZuoraOverride] = useState(false);
  const [zuoraAccountNumber, setZuoraAccountNumber] = useState("");
  const [zuoraSubscriptionNumber, setZuoraSubscriptionNumber] = useState("");
  const [zuoraAccountId, setZuoraAccountId] = useState("");
  const [zuoraSubscriptionId, setZuoraSubscriptionId] = useState("");

  const resetZuoraOverrideFields = () => {
    setShowZuoraOverride(false);
    setZuoraAccountNumber("");
    setZuoraSubscriptionNumber("");
    setZuoraAccountId("");
    setZuoraSubscriptionId("");
  };

  const handleSyncZuoraData = () => {
    setShowSyncLoading(true);
    syncWithZuora(
      {
        subscriptionId,
        ...(showZuoraOverride && {
          zuoraAccountNumber,
          zuoraSubscriptionNumber,
          zuoraAccountId,
          zuoraSubscriptionId,
        }),
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        setOpenSyncSuccessAlert(true);
        refreshOrgData();
      })
      .catch(showError)
      .finally(() => {
        setShowSyncLoading(false);
        resetZuoraOverrideFields();
      });
  };

  const handleSyncAlertClose = (
    _event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined,
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSyncSuccessAlert(false);
  };

  return (
    <>
      {showSyncLoading ? (
        <CircularProgress
          color="primary"
          variant="indeterminate"
          thickness={4}
          size={20}
          data-testid="sync-zuora-loading"
          sx={{ verticalAlign: "middle" }}
        />
      ) : (
        <Tooltip title="Sync all Zuora data">
          <IconButton
            size="small"
            onClick={() => setOpenSyncConfirmDialog(true)}
            aria-label="Sync all Zuora data"
            data-testid={testId}
          >
            <ZuoraLogoSvg size={iconSize} />
          </IconButton>
        </Tooltip>
      )}

      <ConfirmDialog
        title="Sync All Zuora Data"
        open={openSyncConfirmDialog}
        width="md"
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        setOpen={(open) => {
          setOpenSyncConfirmDialog(open);
          if (!open) resetZuoraOverrideFields();
        }}
        onConfirm={() => handleSyncZuoraData()}
        disabled={
          showZuoraOverride &&
          (!zuoraAccountNumber ||
            !zuoraSubscriptionNumber ||
            !zuoraAccountId ||
            !zuoraSubscriptionId)
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography data-testid="sync-zuora-dialog-text">
              This will refresh all subscription and account information from
              Zuora for this organization.
              <br />
              Are you sure you want to <b>sync</b> all Zuora data?
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showZuoraOverride}
                  onChange={(e) => setShowZuoraOverride(e.target.checked)}
                  data-testid="sync-zuora-override-checkbox"
                />
              }
              label="Override Zuora account and subscription"
            />
          </Grid>
          <Grid item xs={12}>
            <Collapse in={showZuoraOverride}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert severity="warning">
                    All 4 fields are required. The Zuora account and
                    subscription must belong to this organization. Existing
                    Zuora data on this subscription will be overwritten.
                  </Alert>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Account Number"
                    placeholder="e.g. A00209559"
                    value={zuoraAccountNumber}
                    onChange={(e) => setZuoraAccountNumber(e.target.value)}
                    data-testid="sync-zuora-account-number"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Subscription Number"
                    placeholder="e.g. A-S00209559"
                    value={zuoraSubscriptionNumber}
                    onChange={(e) => setZuoraSubscriptionNumber(e.target.value)}
                    data-testid="sync-zuora-subscription-number"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Account ID"
                    placeholder="e.g. 8a8aa0259c326a6b019c43d204686ec4"
                    value={zuoraAccountId}
                    onChange={(e) => setZuoraAccountId(e.target.value)}
                    data-testid="sync-zuora-account-id"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Subscription ID"
                    placeholder="e.g. 541bd0c85419cd6bdd9cdae9de8d02d5"
                    value={zuoraSubscriptionId}
                    onChange={(e) => setZuoraSubscriptionId(e.target.value)}
                    data-testid="sync-zuora-subscription-id"
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </Grid>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={openSyncSuccessAlert}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleSyncAlertClose}
      >
        <Alert severity="success" variant="filled">
          Zuora data synced successfully
        </Alert>
      </Snackbar>
    </>
  );
};

export default SyncZuoraButton;
